<template>
  <div
    :class="
      embedded
        ? 'w-full'
        : 'store-users-overlay'
    "
    :role="embedded ? null : 'dialog'"
    :aria-modal="embedded ? null : 'true'"
    @click.self="handleBackdropClick"
  >
    <div
      :class="
        embedded
          ? 'w-full'
          : 'store-users-overlay__inner'
      "
    >
      <div
        :class="[
          'store-users-panel',
          embedded ? 'store-users-panel--embedded' : '',
        ]"
      >
        <div class="store-users-panel__header">
          <div class="store-users-panel__title">
            <hs-icon name="users" tone="primary" soft :size="22" />
            <div>
              <p>Access control</p>
              <h3>Manage store users</h3>
              <span>{{ storeLabel }}</span>
            </div>
          </div>
          <hs-button
            v-if="!embedded"
            type="button"
            variant="secondary"
            icon="x"
            icon-only
            aria-label="Close"
            @click="$emit('close')"
          />
        </div>

        <div
          :class="[
            'store-users-panel__grid',
            hasFormAccess ? 'store-users-panel__grid--with-form' : '',
          ]"
        >
          <section
            :class="[
              'store-users-list',
              hasFormAccess ? 'store-users-list--divided' : '',
            ]"
          >
            <div class="store-users-section-header">
              <div>
                <h4>Current users</h4>
                <p>
                  {{ members.length }} active user{{ members.length === 1 ? "" : "s" }}
                </p>
              </div>
              <hs-button
                type="button"
                variant="secondary"
                size="sm"
                icon="refresh-cw"
                :loading="loadingMembers"
                :disabled="loadingMembers || !canReadUsers"
                @click="loadMembers"
              >
                Refresh
              </hs-button>
            </div>

            <div class="store-users-list__body">
              <div
                v-if="!canReadUsers"
                class="store-users-alert store-users-alert--warning"
              >
                <hs-icon name="shield-off" tone="warning" soft />
                You do not have permission to view store users.
              </div>

              <div v-else-if="loadingMembers" class="store-users-skeletons">
                <div
                  v-for="idx in 4"
                  :key="idx"
                  class="store-users-skeleton"
                ></div>
              </div>

              <hs-empty-state
                v-else-if="members.length === 0"
                icon="users"
                title="No store users"
                description="Add a user with a phone number and permissions to give them access to this store."
              />

              <div
                v-else
                class="store-users-table-shell"
              >
                <table class="store-users-table">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Permissions</th>
                      <th v-if="hasFormAccess">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="member in members"
                      :key="member.user_store_permission_id"
                    >
                      <td>
                        <div class="store-users-person">
                          <div class="store-users-person__avatar">
                            {{ userInitials(member) }}
                          </div>
                          <div>
                            <strong>
                              {{ member.user?.names || member.username || `User #${member.user?.id}` }}
                            </strong>
                            <span>{{ member.user?.phone_number || "No phone" }}</span>
                            <small>{{ member.user?.email || "No email" }}</small>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div class="store-users-permissions">
                          <hs-badge
                            v-for="permission in previewPermissions(member.permissions)"
                            :key="permission"
                            tone="neutral"
                            size="sm"
                          >
                            {{ permissionLabel(permission) }}
                          </hs-badge>
                          <hs-badge
                            v-if="(member.permissions || []).length > 4"
                            tone="primary"
                            size="sm"
                          >
                            +{{ member.permissions.length - 4 }}
                          </hs-badge>
                        </div>
                      </td>
                      <td
                        v-if="hasFormAccess"
                        class="store-users-actions-cell"
                      >
                        <div class="store-users-actions">
                          <hs-button
                            v-if="canWriteUsers || canManageUsers"
                            type="button"
                            variant="secondary"
                            size="sm"
                            icon="edit-3"
                            @click="editMember(member)"
                          >
                            Edit
                          </hs-button>
                          <hs-button
                            v-if="canManageUsers"
                            type="button"
                            variant="danger"
                            size="sm"
                            icon="trash-2"
                            @click="confirmRemove(member)"
                          >
                            Remove
                          </hs-button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </section>

          <aside
            v-if="hasFormAccess"
            class="store-users-editor"
          >
            <div class="store-users-editor__header">
              <hs-icon
                :name="editingMember ? 'user-check' : 'user-plus'"
                tone="primary"
                soft
              />
              <div>
                <h4>{{ editingMember ? "Update user" : "Add user" }}</h4>
                <p>
                  {{ editingMember ? "Adjust identity and permissions." : "Create access for a store team member." }}
                </p>
              </div>
            </div>

            <form class="store-users-form" @submit.prevent="submitForm">
              <div class="store-users-invite-toggle">
                <label>Invite by</label>
                <div class="invite-method-tabs">
                  <button
                    type="button"
                    :class="['invite-tab', { 'invite-tab--active': form.inviteMethod === 'phone' }]"
                    @click="form.inviteMethod = 'phone'"
                  >
                    Phone
                  </button>
                  <button
                    type="button"
                    :class="['invite-tab', { 'invite-tab--active': form.inviteMethod === 'email' }]"
                    @click="form.inviteMethod = 'email'"
                  >
                    Email
                  </button>
                </div>
              </div>

              <hs-input
                v-if="form.inviteMethod === 'phone'"
                v-model.trim="form.phone_number"
                type="tel"
                label="Phone number"
                icon="phone"
                placeholder="+250..."
                required
                :disabled="Boolean(editingMember)"
              />

              <hs-input
                v-else
                v-model.trim="form.email"
                type="email"
                label="Email Address"
                icon="mail"
                placeholder="user@example.com"
                required
              />

              <hs-input
                v-model.trim="form.username"
                type="text"
                label="Username"
                icon="user"
                placeholder="Store role name"
                required
              />

              <hs-select
                v-model="form.rolePreset"
                label="Role preset"
                placeholder="Custom permissions"
                icon="shield"
                :options="roleSelectOptions"
                :hint="loadingRoles ? 'Loading roles...' : ''"
                :disabled="loadingRoles"
                @change="applyRolePreset"
              />

              <div class="store-users-permission-tools">
                <div>
                  <label>Permissions</label>
                  <p>{{ form.permissions.length }} selected</p>
                </div>
                <hs-button
                  type="button"
                  variant="ghost"
                  size="sm"
                  @click="toggleAllPermissions"
                >
                  {{ allSelected ? "Clear all" : "Select all" }}
                </hs-button>
              </div>

              <hs-input
                v-model.trim="permissionSearch"
                type="search"
                icon="search"
                placeholder="Search permissions"
              />

              <div
                v-if="loadingPermissions"
                class="store-users-permission-state"
              >
                Loading permissions...
              </div>

              <div
                v-else-if="filteredPermissionGroups.length === 0"
                class="store-users-permission-state store-users-permission-state--empty"
              >
                No permissions match your search.
              </div>

              <div
                v-else
                class="store-users-permission-list"
              >
                <div
                  v-for="group in filteredPermissionGroups"
                  :key="group.key"
                  class="store-users-permission-group"
                >
                  <div class="store-users-permission-group__title">
                    {{ group.label }}
                  </div>
                  <label
                    v-for="permission in group.options"
                    :key="permission.value"
                    class="store-users-permission-option"
                    :class="{
                      'store-users-permission-option--selected': isPermissionSelected(permission.value),
                    }"
                  >
                    <hs-checkbox
                      :model-value="isPermissionSelected(permission.value)"
                      :label="permission.label"
                      :description="permission.description"
                      @update:modelValue="togglePermission(permission.value, $event)"
                    />
                  </label>
                </div>
              </div>

              <div
                v-if="formError"
                class="store-users-alert store-users-alert--danger"
              >
                <hs-icon name="alert-circle" tone="danger" soft />
                {{ formError }}
              </div>

              <div class="store-users-form__actions">
                <hs-button
                  type="button"
                  variant="secondary"
                  @click="resetForm"
                >
                  {{ editingMember ? "Cancel" : "Reset" }}
                </hs-button>
                <hs-button
                  type="submit"
                  icon="save"
                  :loading="saving"
                  :disabled="saving"
                >
                  {{ editingMember ? "Update user" : "Add user" }}
                </hs-button>
              </div>
            </form>
          </aside>
        </div>
      </div>
    </div>

    <hs-confirm-dialog
      :model-value="removeContext !== null"
      title="Remove store user"
      :message="removeMessage"
      confirm-text="Remove user"
      cancel-text="Cancel"
      tone="danger"
      :loading="removing"
      @confirm="removeMember"
      @update:modelValue="handleRemoveDialogChange"
      @close="removeContext = null"
    />
  </div>
</template>

<script>
import { computed, onMounted, reactive, ref, watch } from "vue";
import { toast } from "vue3-toastify";
import api from "@/helper/api";
import http from "@/helper/http";
import {
  closePermissionDependencies,
  getPermissionSummaryLabel,
  groupPermissionCatalog,
  normalizePermissionName,
  updatePermissionSelection,
} from "@/helper/storePermissions";
import {
  HsBadge,
  HsButton,
  HsCheckbox,
  HsConfirmDialog,
  HsEmptyState,
  HsIcon,
  HsInput,
  HsSelect,
} from "@/components/ui";

const titleCase = (value = "") =>
  String(value)
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const buildRoleOption = (entry) => {
  const name = String(entry?.name || "").trim();
  const id = entry?.id ?? name;
  const permissions = Array.isArray(entry?.permissions)
    ? entry.permissions.map(normalizePermissionName).filter(Boolean)
    : [];

  return {
    id,
    key: String(id),
    name,
    label: name ? titleCase(name.replace(/_/g, " ")) : `Role #${id}`,
    permissions,
  };
};

export default {
  name: "StoreUsersManagementModal",
  components: {
    HsBadge,
    HsButton,
    HsCheckbox,
    HsConfirmDialog,
    HsEmptyState,
    HsIcon,
    HsInput,
    HsSelect,
  },
  props: {
    store: {
      type: Object,
      required: true,
    },
    embedded: {
      type: Boolean,
      default: false,
    },
    canReadUsers: {
      type: Boolean,
      default: true,
    },
    canWriteUsers: {
      type: Boolean,
      default: true,
    },
    canManageUsers: {
      type: Boolean,
      default: true,
    },
  },
  emits: ["close"],
  setup(props, { emit }) {
    const members = ref([]);
    const permissionGroups = ref([]);
    const roleOptions = ref([]);
    const loadingMembers = ref(false);
    const loadingPermissions = ref(false);
    const loadingRoles = ref(false);
    const saving = ref(false);
    const removing = ref(false);
    const editingMember = ref(null);
    const removeContext = ref(null);
    const permissionSearch = ref("");
    const formError = ref("");
    const form = reactive({
      phone_number: "",
      email: "",
      inviteMethod: "phone",
      username: "",
      rolePreset: "",
      permissions: [],
    });

    const storeLabel = computed(() =>
      props.store?.name
        ? `${props.store.name} (#${props.store.id})`
        : `Store #${props.store?.id}`,
    );

    const allPermissions = computed(() =>
      permissionGroups.value.flatMap((group) => group.options),
    );

    const allSelected = computed(
      () =>
        allPermissions.value.length > 0 &&
        allPermissions.value.every((permission) =>
          form.permissions.includes(permission.value),
        ),
    );

    const roleSelectOptions = computed(() => [
      {
        label: "Custom permissions",
        value: "",
      },
      ...roleOptions.value.map((role) => ({
        label: role.label,
        value: role.key,
      })),
    ]);

    const filteredPermissionGroups = computed(() => {
      const query = permissionSearch.value.trim().toLowerCase();
      if (!query) {
        return permissionGroups.value;
      }

      return permissionGroups.value
        .map((group) => ({
          ...group,
          options: group.options.filter((permission) => {
            const label = String(permission.label || "").toLowerCase();
            const value = String(permission.value || "").toLowerCase();
            const groupLabel = String(group.label || "").toLowerCase();
            return (
              label.includes(query) ||
              value.includes(query) ||
              groupLabel.includes(query)
            );
          }),
        }))
        .filter((group) => group.options.length > 0);
    });

    const hasFormAccess = computed(
      () => props.canWriteUsers || props.canManageUsers,
    );

    const removeMessage = computed(() => {
      if (!removeContext.value) {
        return "";
      }
      const user = removeContext.value.user || {};
      return `Remove ${
        user.names || user.phone_number || `user #${user.id}`
      } from ${storeLabel.value}?`;
    });

    const permissionLabel = (permission) => {
      const normalized = normalizePermissionName(permission);
      const option = allPermissions.value.find(
        (item) => item.value === normalized,
      );
      if (option) {
        const group = permissionGroups.value.find((item) =>
          item.options.some((candidate) => candidate.value === normalized),
        );
        return ["TICKET_CARTS", "EXPENSES", "SALE_CARTS"].includes(group?.key)
          ? `${group.label}: ${option.label}`
          : option.label;
      }
      return getPermissionSummaryLabel(normalized);
    };

    const previewPermissions = (permissions = []) => permissions.slice(0, 4);

    const userInitials = (member = {}) => {
      const value =
        member.user?.names ||
        member.username ||
        member.user?.phone_number ||
        "U";
      return String(value)
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part.charAt(0).toUpperCase())
        .join("");
    };

    const resetForm = () => {
      editingMember.value = null;
      form.phone_number = "";
      form.email = "";
      form.inviteMethod = "phone";
      form.username = "";
      form.rolePreset = "";
      form.permissions = [];
      permissionSearch.value = "";
      formError.value = "";
    };

    const handleBackdropClick = () => {
      if (!props.embedded) {
        emit("close");
      }
    };

    const loadMembers = async () => {
      if (!props.canReadUsers) {
        members.value = [];
        return;
      }

      loadingMembers.value = true;
      try {
        const response = await http.get(
          api.store_users_permissions(props.store.id),
        );
        members.value = Array.isArray(response?.data) ? response.data : [];
      } catch (error) {
        toast.error(
          error?.response?.data?.error?.message || "Unable to load store users.",
        );
        members.value = [];
      } finally {
        loadingMembers.value = false;
      }
    };

    const loadPermissionCatalog = async () => {
      loadingPermissions.value = true;
      try {
        const response = await http.get(api.store_permissions_catalog);
        permissionGroups.value = groupPermissionCatalog(response?.data || {});
      } catch (error) {
        toast.error(
          error?.response?.data?.error?.message ||
            "Unable to load store permissions.",
        );
      } finally {
        loadingPermissions.value = false;
      }
    };

    const loadRoles = async () => {
      loadingRoles.value = true;
      try {
        const response = await http.get(api.store_roles);
        roleOptions.value = (Array.isArray(response?.data) ? response.data : [])
          .map(buildRoleOption)
          .filter((role) => role.permissions.length > 0)
          .sort((a, b) => a.label.localeCompare(b.label));
        if (editingMember.value) {
          form.rolePreset = findMatchingRolePreset(form.permissions);
        }
      } catch (error) {
        toast.error(
          error?.response?.data?.error?.message || "Unable to load store roles.",
        );
        roleOptions.value = [];
      } finally {
        loadingRoles.value = false;
      }
    };

    const editMember = (member) => {
      if (!props.canWriteUsers && !props.canManageUsers) {
        toast.error("You do not have permission to update store users.");
        return;
      }

      editingMember.value = member;
      form.phone_number = member.user?.phone_number || "";
      form.email = member.user?.email || "";
      form.inviteMethod = member.user?.email ? "email" : "phone";
      form.username =
        member.username || member.user?.names || member.user?.phone_number || "";
      form.permissions = closePermissionDependencies(member.permissions || []);
      form.rolePreset = findMatchingRolePreset(form.permissions);
      formError.value = "";
    };

    const normalizeSelectedPermissions = (permissions = form.permissions) =>
      closePermissionDependencies(permissions);

    const findMatchingRolePreset = (permissions) => {
      const selectedPermissions = normalizeSelectedPermissions(permissions).sort();
      const selectedValue = selectedPermissions.join("|");
      const matchingRole = roleOptions.value.find((role) => {
        const roleValue = normalizeSelectedPermissions(role.permissions)
          .sort()
          .join("|");
        return roleValue && roleValue === selectedValue;
      });
      return matchingRole?.key || "";
    };

    const applyRolePreset = () => {
      if (!form.rolePreset) {
        return;
      }

      const role = roleOptions.value.find((item) => item.key === form.rolePreset);
      if (!role) {
        return;
      }

      form.permissions = closePermissionDependencies(role.permissions);
    };

    const isPermissionSelected = (permission) =>
      form.permissions.includes(normalizePermissionName(permission));

    const togglePermission = (permission, selected) => {
      const normalized = normalizePermissionName(permission);
      if (!normalized) {
        return;
      }

      form.permissions = updatePermissionSelection(
        form.permissions,
        normalized,
        selected,
        allPermissions.value.map((item) => item.value),
      );
      form.rolePreset = findMatchingRolePreset(form.permissions);
    };

    const toggleAllPermissions = () => {
      form.permissions = allSelected.value
        ? []
        : closePermissionDependencies(
            allPermissions.value.map((permission) => permission.value),
          );
      form.rolePreset = findMatchingRolePreset(form.permissions);
    };

    const submitForm = async () => {
      formError.value = "";
      if (!props.canWriteUsers && !props.canManageUsers) {
        formError.value = "You do not have permission to save store users.";
        return;
      }

      if (!form.username || form.permissions.length === 0) {
        formError.value = "Username and permissions are required.";
        return;
      }

      if (form.inviteMethod === "phone" && !form.phone_number) {
        formError.value = "Phone number is required.";
        return;
      }

      if (form.inviteMethod === "email" && !form.email) {
        formError.value = "Email address is required.";
        return;
      }

      saving.value = true;
      try {
        const payload = {
          store_id: props.store.id,
          username: form.username,
          permissions: normalizeSelectedPermissions(),
        };

        if (form.inviteMethod === "phone") {
          payload.phone_number = form.phone_number;
        } else {
          payload.email = form.email;
        }

        await http.post(api.assign_store_user_permissions, payload);
        toast.success(
          editingMember.value ? "Store user updated." : "Store user added.",
        );
        resetForm();
        await loadMembers();
      } catch (error) {
        formError.value =
          error?.response?.data?.error?.message || "Unable to save store user.";
      } finally {
        saving.value = false;
      }
    };

    const confirmRemove = (member) => {
      if (!props.canManageUsers) {
        toast.error("You do not have permission to remove store users.");
        return;
      }

      removeContext.value = member;
    };

    const handleRemoveDialogChange = (open) => {
      if (!open) {
        removeContext.value = null;
      }
    };

    const removeMember = async () => {
      if (!props.canManageUsers) {
        toast.error("You do not have permission to remove store users.");
        return;
      }

      if (!removeContext.value) {
        return;
      }
      removing.value = true;
      try {
        const removedPermissionId = removeContext.value.user_store_permission_id;
        await http.delete(api.delete_store_user_permissions, {
          data: {
            user_store_permission_id: removedPermissionId,
          },
        });
        toast.success("Store user removed.");
        removeContext.value = null;
        if (
          editingMember.value?.user_store_permission_id === removedPermissionId
        ) {
          resetForm();
        }
        await loadMembers();
      } catch (error) {
        toast.error(
          error?.response?.data?.error?.message ||
            "Unable to remove store user.",
        );
      } finally {
        removing.value = false;
      }
    };

    onMounted(() => {
      loadPermissionCatalog();
      loadRoles();
      if (props.canReadUsers) {
        loadMembers();
      }
    });

    watch(
      () => [props.store?.id, props.canReadUsers],
      ([storeId, canRead]) => {
        resetForm();
        if (storeId && canRead) {
          loadMembers();
        } else {
          members.value = [];
        }
      },
    );

    return {
      allSelected,
      applyRolePreset,
      confirmRemove,
      editMember,
      editingMember,
      filteredPermissionGroups,
      form,
      formError,
      handleBackdropClick,
      handleRemoveDialogChange,
      hasFormAccess,
      isPermissionSelected,
      loadMembers,
      loadingMembers,
      loadingPermissions,
      loadingRoles,
      members,
      permissionGroups,
      permissionLabel,
      permissionSearch,
      previewPermissions,
      removeContext,
      removeMember,
      removeMessage,
      removing,
      resetForm,
      roleSelectOptions,
      roleOptions,
      saving,
      storeLabel,
      submitForm,
      toggleAllPermissions,
      togglePermission,
      userInitials,
    };
  },
};
</script>

<style scoped>
.store-users-overlay {
  background:
    radial-gradient(circle at 20% 6%, color-mix(in srgb, var(--hs-primary, #3293ec) 18%, transparent), transparent 30%),
    rgba(15, 23, 42, 0.62);
  backdrop-filter: blur(10px);
  inset: 0;
  overflow-y: auto;
  padding: 1.5rem 1rem;
  position: fixed;
  z-index: 5100;
}

.store-users-overlay__inner {
  align-items: center;
  display: flex;
  margin: 0 auto;
  min-height: calc(100vh - 3rem);
  max-width: 72rem;
  width: 100%;
}

.store-users-panel {
  background:
    radial-gradient(circle at 88% 0%, color-mix(in srgb, var(--hs-primary, #3293ec) 8%, transparent), transparent 32%),
    color-mix(in srgb, var(--hs-surface, #ffffff) 97%, transparent);
  border: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22));
  border-radius: 26px;
  box-shadow: 0 26px 80px rgba(15, 23, 42, 0.2);
  overflow: hidden;
  position: relative;
  width: 100%;
}

.store-users-panel--embedded {
  box-shadow: 0 18px 44px rgba(15, 23, 42, 0.08);
}

.store-users-panel__header {
  align-items: center;
  border-bottom: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22));
  display: flex;
  gap: 1rem;
  justify-content: space-between;
  padding: 1.15rem 1.25rem;
}

.store-users-panel__title {
  align-items: center;
  display: flex;
  gap: 0.9rem;
  min-width: 0;
}

.store-users-panel__title h3,
.store-users-editor__header h4,
.store-users-section-header h4 {
  color: var(--hs-text, #0f172a);
  font-weight: 950;
  letter-spacing: 0;
  margin: 0;
}

.store-users-panel__title h3 {
  font-size: 1.3rem;
  line-height: 1.12;
}

.store-users-panel__title p {
  color: var(--hs-primary, #3293ec);
  font-size: 0.72rem;
  font-weight: 950;
  letter-spacing: 0.11em;
  margin: 0 0 0.2rem;
  text-transform: uppercase;
}

.store-users-panel__title span,
.store-users-section-header p,
.store-users-editor__header p,
.store-users-permission-tools p {
  color: var(--hs-muted, #64748b);
  font-size: 0.82rem;
  font-weight: 800;
}

.store-users-panel__grid {
  display: grid;
}

.store-users-panel__grid--with-form {
  grid-template-columns: minmax(0, 1fr) minmax(22rem, 24rem);
}

.store-users-list {
  min-height: 34rem;
}

.store-users-list--divided {
  border-right: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22));
}

.store-users-section-header {
  align-items: center;
  border-bottom: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22));
  display: flex;
  flex-wrap: wrap;
  gap: 0.9rem;
  justify-content: space-between;
  padding: 1rem 1.25rem;
}

.store-users-section-header h4,
.store-users-editor__header h4 {
  font-size: 0.98rem;
}

.store-users-section-header p,
.store-users-editor__header p,
.store-users-permission-tools p {
  margin: 0.25rem 0 0;
}

.store-users-list__body {
  padding: 1.25rem;
}

.store-users-alert {
  align-items: center;
  border: 1px solid;
  border-radius: 18px;
  display: flex;
  gap: 0.8rem;
  padding: 1rem;
  font-size: 0.88rem;
  font-weight: 850;
}

.store-users-alert--warning {
  background: color-mix(in srgb, #f59e0b 11%, transparent);
  border-color: color-mix(in srgb, #f59e0b 28%, transparent);
  color: #92400e;
}

.store-users-alert--danger {
  background: color-mix(in srgb, #ef4444 10%, transparent);
  border-color: color-mix(in srgb, #ef4444 28%, transparent);
  color: #b91c1c;
}

.store-users-skeletons {
  display: grid;
  gap: 0.7rem;
}

.store-users-skeleton {
  animation: store-users-pulse 1.25s ease-in-out infinite;
  background: color-mix(in srgb, var(--hs-muted, #64748b) 10%, transparent);
  border-radius: 18px;
  height: 4.2rem;
}

@keyframes store-users-pulse {
  50% {
    opacity: 0.45;
  }
}

.store-users-table-shell {
  border: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22));
  border-radius: 20px;
  overflow-x: auto;
}

.store-users-table {
  border-collapse: collapse;
  min-width: 46rem;
  width: 100%;
}

.store-users-table thead {
  background: color-mix(in srgb, var(--hs-bg, #f9fafb) 82%, var(--hs-surface, #ffffff));
}

.store-users-table th {
  color: var(--hs-muted, #64748b);
  font-size: 0.72rem;
  font-weight: 950;
  letter-spacing: 0.07em;
  padding: 0.85rem 1rem;
  text-align: left;
  text-transform: uppercase;
}

.store-users-table th:last-child {
  text-align: right;
}

.store-users-table td {
  border-top: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22));
  padding: 0.95rem 1rem;
  vertical-align: middle;
}

.store-users-table tbody tr {
  transition: background 0.18s ease;
}

.store-users-table tbody tr:hover {
  background: color-mix(in srgb, var(--hs-primary, #3293ec) 4%, transparent);
}

.store-users-person {
  align-items: center;
  display: flex;
  gap: 0.8rem;
  min-width: 0;
}

.store-users-person__avatar {
  align-items: center;
  background: linear-gradient(135deg, var(--hs-primary, #3293ec), var(--hs-primary-strong, #1d4ed8));
  border-radius: 14px;
  color: #fff;
  display: inline-flex;
  flex-shrink: 0;
  font-size: 0.8rem;
  font-weight: 950;
  height: 2.55rem;
  justify-content: center;
  width: 2.55rem;
}

.store-users-person strong {
  color: var(--hs-text, #0f172a);
  display: block;
  font-size: 0.9rem;
  font-weight: 950;
}

.store-users-person span,
.store-users-person small {
  color: var(--hs-muted, #64748b);
  display: block;
  font-size: 0.76rem;
  font-weight: 750;
}

.store-users-permissions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  max-width: 28rem;
}

.store-users-actions-cell {
  text-align: right;
}

.store-users-actions {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  justify-content: flex-end;
}

.store-users-editor {
  background: color-mix(in srgb, var(--hs-bg, #f9fafb) 76%, var(--hs-surface, #ffffff));
  padding: 1.25rem;
}

.store-users-editor__header {
  align-items: center;
  display: flex;
  gap: 0.8rem;
  margin-bottom: 1.1rem;
}

.store-users-form {
  display: grid;
  gap: 1rem;
}

.store-users-permission-tools {
  align-items: center;
  display: flex;
  gap: 0.8rem;
  justify-content: space-between;
}

.store-users-permission-tools label,
.store-users-permission-group__title {
  color: var(--hs-text, #0f172a);
  font-size: 0.82rem;
  font-weight: 950;
}

.store-users-permission-list,
.store-users-permission-state {
  background: color-mix(in srgb, var(--hs-surface, #ffffff) 92%, transparent);
  border: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22));
  border-radius: 18px;
}

.store-users-permission-list {
  display: grid;
  gap: 0.9rem;
  max-height: 20rem;
  overflow-y: auto;
  padding: 0.75rem;
}

.store-users-permission-state {
  color: var(--hs-muted, #64748b);
  font-size: 0.86rem;
  font-weight: 850;
  padding: 1rem;
}

.store-users-permission-state--empty {
  border-style: dashed;
  text-align: center;
}

.store-users-permission-group {
  display: grid;
  gap: 0.5rem;
}

.store-users-permission-group__title {
  color: var(--hs-muted, #64748b);
  letter-spacing: 0.07em;
  text-transform: uppercase;
}

.store-users-permission-option {
  background: color-mix(in srgb, var(--hs-surface, #ffffff) 96%, transparent);
  border: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22));
  border-radius: 15px;
  cursor: pointer;
  display: block;
  padding: 0.72rem;
  transition: border-color 0.18s ease, background 0.18s ease, box-shadow 0.18s ease;
}

.store-users-permission-option--selected {
  background: color-mix(in srgb, var(--hs-primary, #3293ec) 8%, var(--hs-surface, #ffffff));
  border-color: color-mix(in srgb, var(--hs-primary, #3293ec) 34%, var(--hs-border, rgba(148, 163, 184, 0.22)));
  box-shadow: 0 10px 22px color-mix(in srgb, var(--hs-primary, #3293ec) 10%, transparent);
}

.store-users-form__actions {
  align-items: center;
  display: flex;
  gap: 0.8rem;
  justify-content: space-between;
  padding-top: 0.25rem;
}

@media (max-width: 1024px) {
  .store-users-panel__grid--with-form {
    grid-template-columns: 1fr;
  }

  .store-users-list--divided {
    border-right: 0;
    border-bottom: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22));
  }
}

@media (max-width: 640px) {
  .store-users-overlay {
    padding: 0.75rem;
  }

  .store-users-panel {
    border-radius: 22px;
  }

  .store-users-panel__header,
  .store-users-list__body,
  .store-users-editor {
    padding: 1rem;
  }

  .store-users-form__actions {
    align-items: stretch;
    flex-direction: column-reverse;
  }

  .store-users-form__actions :deep(.ui-button) {
    width: 100%;
  }
}

.store-users-invite-toggle {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.store-users-invite-toggle label {
  color: var(--hs-text, #0f172a);
  font-size: 0.82rem;
  font-weight: 950;
}

.invite-method-tabs {
  display: flex;
  gap: 0.5rem;
  background: color-mix(in srgb, var(--hs-bg, #f9fafb) 95%, transparent);
  padding: 0.5rem;
  border-radius: 12px;
  width: 100%;
  border: 1px solid var(--hs-border, rgba(148, 163, 184, 0.15));
}

.invite-tab {
  flex: 1;
  padding: 0.65rem 1.1rem;
  border: 1px solid transparent;
  border-radius: 9px;
  font-weight: 500;
  font-size: 0.88rem;
  cursor: pointer;
  transition: all 0.2s ease;
  background: transparent;
  color: var(--hs-muted, #64748b);
}

.invite-tab:hover:not(.invite-tab--active) {
  background: color-mix(in srgb, var(--hs-muted, #64748b) 6%, transparent);
  color: var(--hs-text, #0f172a);
}

.invite-tab--active {
  background: color-mix(
    in srgb,
    var(--hs-primary, #3293ec) 14%,
    var(--hs-surface, #fff)
  );
  color: var(--hs-primary-strong, #1e40af);
  border-color: color-mix(
    in srgb,
    var(--hs-primary, #3293ec) 38%,
    var(--hs-border, transparent)
  );
  box-shadow: 0 1px 3px color-mix(in srgb, var(--hs-primary, #3293ec) 12%, transparent);
  font-weight: 700;
}

.invite-tab:focus-visible {
  outline: 2px solid var(--hs-primary, #3293ec);
  outline-offset: 2px;
}
</style>
